'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import type { LumiEmotion } from './lumiConstants';

/**
 * LumiLynx — der neue Lumi (Kristall-Luchs-Kopf) als reaktiver Begleiter.
 *
 * Augenloser Kopf-PNG + getunte SVG-Augen (im /dev/lumi-Lab gefeilt) → die Augen
 * BLINZELN (Lid fährt runter) und tragen die Emotion (openness), dazu Glow-Farbe
 * + Float/Scale. Ersetzt den alten Blob (LumiCrystal/LumiBody) bei gleicher API:
 * `size`, optional `emotion` (8 Zustände wie gehabt) und `animate`.
 */

interface Eye {
  cx: number; cy: number; tilt: number;
  eyeW: number; eyeH: number; outlineW: number;
  lTipX: number; lTipY: number; rTipX: number; rTipY: number;
  lidTopH: number; lidBotY: number; lidApexX: number;
  pupilW: number; pupilH: number; pupilX: number; pupilY: number;
  glintX: number; glintY: number; glintR: number;
  darkLO: number; darkLI: number; darkRI: number; darkRO: number;
}

const C = { iris: '#9fcc3b', irisDark: '#6a9e2b', pupilColor: '#06302f', glint: '#ffffff', outline: '#06302f' };

// Gelockte Augen-Geometrie (aus /dev/lumi).
const HEAD_L: Eye = {
  cx: 36.5, cy: 53.5, tilt: 15, eyeW: 13.25, eyeH: 16.5, outlineW: 1.1,
  lTipX: -8.75, lTipY: 1.25, rTipX: 8.25, rTipY: 1.75, lidTopH: 11, lidBotY: -10, lidApexX: 0.5,
  pupilW: 7, pupilH: 7, pupilX: -0.25, pupilY: -1.75, glintX: -1.7, glintY: -1.5, glintR: 1,
  darkLO: -2, darkLI: 1.25, darkRI: 1.25, darkRO: -2.25,
};
const HEAD_R: Eye = {
  cx: 63.5, cy: 53.5, tilt: -15, eyeW: 13.25, eyeH: 16.5, outlineW: 1.1,
  lTipX: -8.25, lTipY: 1.75, rTipX: 8.75, rTipY: 1.25, lidTopH: 11, lidBotY: -10, lidApexX: -0.5,
  pupilW: 7, pupilH: 7, pupilX: 0.25, pupilY: -1.75, glintX: -1.2, glintY: -1.9, glintR: 1,
  darkLO: -2.75, darkLI: 1.5, darkRI: 1.25, darkRO: -0.75,
};

// Ganzkörper-Augen — aus dem Kopf-Config abgeleitet (×0.65, neu positioniert auf die
// gemessenen Augen im Körperbild). Im Lab /dev/lumi (Ziel „Körper") feinjustierbar.
const BODY_L: Eye = {
  cx: 40.9, cy: 36, tilt: 15, eyeW: 8.61, eyeH: 10.73, outlineW: 0.72,
  lTipX: -5.69, lTipY: 0.81, rTipX: 5.36, rTipY: 1.14, lidTopH: 7.15, lidBotY: -6.5, lidApexX: 0.33,
  pupilW: 4.55, pupilH: 4.55, pupilX: -0.16, pupilY: -1.14, glintX: -1.11, glintY: -0.98, glintR: 0.65,
  darkLO: -1.3, darkLI: 0.81, darkRI: 0.81, darkRO: -1.46,
};
const BODY_R: Eye = {
  cx: 59.1, cy: 36, tilt: -15, eyeW: 8.61, eyeH: 10.73, outlineW: 0.72,
  lTipX: -5.36, lTipY: 1.14, rTipX: 5.69, rTipY: 0.81, lidTopH: 7.15, lidBotY: -6.5, lidApexX: -0.33,
  pupilW: 4.55, pupilH: 4.55, pupilX: 0.16, pupilY: -1.14, glintX: -0.78, glintY: -1.24, glintR: 0.65,
  darkLO: -1.79, darkLI: 0.98, darkRI: 0.81, darkRO: -0.49,
};

const VARIANT = {
  head: { img: '/lumi-head-noeyes.png', L: HEAD_L, R: HEAD_R, glowY: 45 },
  body: { img: '/lumi-lynx-noeyes.png', L: BODY_L, R: BODY_R, glowY: 33 },
} as const;

interface EmoCfg {
  glow: string; openness: number;
  /** Schwebe-Parameter — wirken nur mit `float`-Prop (Opt-in): Amplitude px · Zyklus s · Atem-Scale. */
  floatA: number; floatD: number; scale: [number, number];
  /** Ausdrucks-Deltas auf die gelockte Augen-Geometrie (Default = neutral → 0 Regression).
   *  Im /dev/lumi-Lab tunen, dann hier einbacken. pupilScale ×, gaze in Augen-Koordinaten,
   *  lidSkew in Grad (links +, rechts gespiegelt — symmetrischer Ausdruck). */
  pupilScale?: number; gazeX?: number; gazeY?: number; lidSkew?: number;
}
const LYNX_EMOTION: Record<LumiEmotion, EmoCfg> = {
  idle: { glow: '#16a39e', openness: 1, floatA: 3, floatD: 4, scale: [0.99, 1.01] },
  speaking: { glow: '#0e7c7b', openness: 1, floatA: 2, floatD: 3, scale: [0.98, 1.02] },
  celebrating: { glow: '#9fcc3b', openness: 1, floatA: 6, floatD: 1.6, scale: [0.95, 1.06] },
  encouraging: { glow: '#16a39e', openness: 1, floatA: 4, floatD: 3, scale: [0.98, 1.03] },
  thinking: { glow: '#6fc9c5', openness: 0.86, floatA: 1.5, floatD: 5, scale: [0.99, 1.01] },
  warning: { glow: '#f59e0b', openness: 0.82, floatA: 2, floatD: 3.5, scale: [0.99, 1.01] },
  'streak-fire': { glow: '#f97316', openness: 1, floatA: 4, floatD: 2, scale: [0.97, 1.04] },
  sleeping: { glow: '#5a7a78', openness: 0.04, floatA: 1.5, floatD: 6, scale: [0.99, 1.01] },
};

function OneEye({
  e, openness, idp, pupilScale = 1, gazeX = 0, gazeY = 0, lidSkew = 0,
}: {
  e: Eye; openness: number; idp: string;
  /** Ausdrucks-Deltas (neutral = 1/0/0/0 → exakt die gelockte Geometrie). */
  pupilScale?: number; gazeX?: number; gazeY?: number; lidSkew?: number;
}) {
  const hw = e.eyeW / 2;
  const hh = e.eyeH / 2;
  const eyePath = `M ${-hw} 0 Q 0 ${-hh} ${hw} 0 Q 0 ${hh} ${-hw} 0 Z`;
  const lidBotY = e.lidBotY + (1 - openness) * e.eyeH;
  const lidPath =
    `M ${e.lTipX} ${e.lTipY} Q ${e.lidApexX} ${-e.lidTopH} ${e.rTipX} ${e.rTipY} ` +
    `Q ${e.lidApexX} ${lidBotY} ${e.lTipX} ${e.lTipY} Z`;
  const clip = `eye-${idp}`;
  // lidSkew gespiegelt je Seite (tilt-Vorzeichen = Seite) → symmetrischer Ausdruck;
  // Gaze NICHT gespiegelt (beide Pupillen blicken in dieselbe Bildschirm-Richtung).
  const side = e.tilt >= 0 ? 1 : -1;
  const px = e.pupilX + gazeX;
  const py = e.pupilY + gazeY;
  return (
    <g transform={`translate(${e.cx} ${e.cy}) rotate(${e.tilt})`}>
      <defs><clipPath id={clip}><path d={eyePath} /></clipPath></defs>
      <g clipPath={`url(#${clip})`}>
        <rect x={-hw - 1} y={-hh - 1} width={hw * 2 + 2} height={hh * 2 + 2} fill={C.iris} />
        <polygon points={`${-hw - 2},${-hh - 2} ${e.pupilX},${-hh - 2} ${e.pupilX},${e.darkLI} ${-hw - 2},${e.darkLO}`} fill={C.irisDark} />
        <polygon points={`${e.pupilX},${-hh - 2} ${hw + 2},${-hh - 2} ${hw + 2},${e.darkRO} ${e.pupilX},${e.darkRI}`} fill={C.irisDark} />
        <ellipse cx={px} cy={py} rx={(e.pupilW / 2) * pupilScale} ry={(e.pupilH / 2) * pupilScale} fill={C.pupilColor} />
        {/* Glint bleibt fix (Reflexion wandert nicht mit der Pupille). */}
        <circle cx={e.glintX} cy={e.glintY} r={e.glintR} fill={C.glint} />
      </g>
      <path d={eyePath} fill="none" stroke={C.outline} strokeWidth={e.outlineW} strokeLinejoin="round" />
      <path d={lidPath} transform={lidSkew ? `rotate(${lidSkew * side})` : undefined} fill={C.outline} stroke={C.outline} strokeWidth={0.3} strokeLinejoin="miter" strokeMiterlimit={10} />
    </g>
  );
}

interface LumiLynxProps {
  size?: number;
  emotion?: LumiEmotion;
  animate?: boolean;
  className?: string;
  /** Kopf in einen runden Avatar EINPASSEN (skalieren + clippen). Aus = voller Kopf. */
  frame?: boolean;
  /** `head` = Kristall-Luchs-Kopf (Default), `body` = ganzer Luchs (Splash/Stage). */
  variant?: 'head' | 'body';
  /** Weiche Aura hinter Lumi. Default nur bei `head` (Avatar) — bei der
   *  freistehenden Vollfigur würde der runde Radial-Glow als Kreis sichtbar. */
  glow?: boolean;
  /** Sanftes Schweben + Atmen (floatA/floatD/scale der Emotion). OPT-IN — Default aus,
   *  damit Avatare/Chrome ruhig bleiben; gedacht für fokale Momente (Splash/Stage/Coach). */
  float?: boolean;
}

export function LumiLynx({ size = 56, emotion = 'idle', animate = true, className = '', frame = false, variant = 'head', glow, float = false }: LumiLynxProps) {
  const cfg = LYNX_EMOTION[emotion];
  const v = VARIANT[variant];
  const showGlow = glow ?? variant === 'head';
  const reduce = useReducedMotion();
  const uid = useId();
  const [blink, setBlink] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!animate || reduce || emotion === 'sleeping') return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    let dblTimer: ReturnType<typeof setTimeout>;
    const animateBlink = () => {
      const dur = 120 + Math.random() * 80; // 120–200ms, leicht variabel
      const start = performance.now();
      const stepFn = (t: number) => {
        if (!alive) return;
        const p = Math.min(1, (t - start) / dur);
        setBlink(p < 0.5 ? p * 2 : (1 - p) * 2);
        if (p < 1) rafRef.current = requestAnimationFrame(stepFn);
        else setBlink(0);
      };
      rafRef.current = requestAnimationFrame(stepFn);
    };
    const schedule = () => {
      // Unregelmäßig: meist 2.4–6s, ab und zu eine deutlich längere Pause.
      const gap = 2400 + Math.random() * 3600 + (Math.random() < 0.25 ? Math.random() * 3500 : 0);
      timer = setTimeout(() => {
        if (!alive) return;
        animateBlink();
        // ~18% Doppel-Blinzeln (sehr menschlich).
        if (Math.random() < 0.18) {
          dblTimer = setTimeout(() => { if (alive) animateBlink(); }, 210 + Math.random() * 130);
        }
        schedule();
      }, gap);
    };
    schedule();
    return () => {
      alive = false;
      clearTimeout(timer);
      clearTimeout(dblTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setBlink(0);
    };
  }, [animate, reduce, emotion]);

  const blinkOn = animate && !reduce && emotion !== 'sleeping';
  const openness = cfg.openness * (1 - (blinkOn ? blink : 0));

  // Schwebe/Atmen (Opt-in): nutzt die bislang ungenutzten floatA/floatD/scale der Emotion.
  // Die Bewegungs-Weiche läuft über CSS (motion-safe), nicht über `reduce`: der
  // Server kennt die Einstellung nicht, ein davon abhängiger Style wich beim
  // Hydrieren vom Server-HTML ab (Befund Planseite 25.10.2026).
  const floatOn = float && animate;
  const floatStyle = floatOn
    ? ({
        '--lumi-float-d': `${cfg.floatD}s`,
        '--lumi-float-a': `${cfg.floatA}px`,
        '--lumi-s-min': String(cfg.scale[0]),
        '--lumi-s-max': String(cfg.scale[1]),
      } as React.CSSProperties)
    : undefined;
  const floatClass = floatOn ? 'motion-safe:animate-[lumi-float_var(--lumi-float-d)_ease-in-out_infinite]' : '';

  return (
    <div className={`relative ${frame ? 'overflow-hidden' : ''} ${floatClass} ${className}`} style={{ width: size, height: size, ...floatStyle }} aria-hidden>
      {showGlow && (
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: `radial-gradient(circle at 50% ${v.glowY}%, ${cfg.glow}55 0%, transparent 65%)` }}
        />
      )}
      {/* Voller Kopf/Körper (object-contain): die Figur reicht bis zur Unterkante, die
          Ohren sind oben drin. Im Avatar (frame) noch ein kleines Stück nach unten,
          damit die Ohrspitzen etwas Luft zum oberen Rand haben. Bild + Augen-SVG
          teilen denselben 0–100-Raum, daher sitzen die Augen exakt. */}
      <div className="absolute inset-0" style={frame ? { transform: 'translateY(5%)' } : undefined}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={v.img} alt="" className="absolute inset-0 h-full w-full object-contain select-none" draggable={false} />
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
          <OneEye e={v.L} openness={openness} idp={`${uid}-L`} pupilScale={cfg.pupilScale} gazeX={cfg.gazeX} gazeY={cfg.gazeY} lidSkew={cfg.lidSkew} />
          <OneEye e={v.R} openness={openness} idp={`${uid}-R`} pupilScale={cfg.pupilScale} gazeX={cfg.gazeX} gazeY={cfg.gazeY} lidSkew={cfg.lidSkew} />
        </svg>
      </div>
    </div>
  );
}
